import { ReactNode, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { clone, isString, keys } from 'lodash';
import { useMutation } from '@apollo/client';

import ELEMENT, { createTestId } from 'common/element';
import { MenuItemConfig, PlotModel } from 'common/types';
import { AlertInput, Permission, SavedSearchType } from 'common/graphqlTypes';
import { DownloadConfig, downloadFile, isSafari } from 'service/browser';
import { useCopyShortLink } from 'hooks/useCopyShortLink';

import { CREATE_ALERT } from 'components/alerts/queries';
import SaveSearchDialog from 'components/search/SaveSearchDialog';
import SaveAlertDialog, { DEFAULT_ALERT, TriggerType } from 'components/alerts/SaveAlertDialog';
import SaveToDashboard from 'components/dashboards/DashboardPage/modals/SaveToDashboard';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import Dropdown from 'components/common/Dropdown/Dropdown';
import Link from 'components/common/Link/Link';
import useAlertPageAlerts from 'components/alerts/useAlertPageAlerts';
import { ButtonVariant } from 'components/common/Button/Button';
import {
  absoluteResolvedUrlState,
  bulkExportsPageUrlState,
  completeFilterState,
} from 'components/common/Managers/RouteStateManager.state';

// predefined search actions
export enum SearchActions {
  ALERT                         = 'alert',
  COPY_LINK                     = 'copy',
  DASHBOARD                     = 'dashboard',
  DOWNLOAD                      = 'download',
  DOWNLOAD_CSV                  = 'download_csv',
  EXPORT                        = 'export',
  SAVE                          = 'save',
  SAVE_SHARED                   = 'shared',
}

// types
type SaveAlertDialogConfig = {
  title?:       string;
  triggerType?: TriggerType;
};

type SaveDashboardDialogConfig = {
  title?: string;
};

export type ActionMenuItemConfig = MenuItemConfig & { action?: SearchActions };

// props
type SearchActionsDropdownProps = {
  text:                          string;
  className?:                    string;
  variant?:                      ButtonVariant;
  leftIcon?:                     ReactNode;
  sections:                      Array<Array<string | ActionMenuItemConfig>>;
  saveAlertDialogConfig?:        SaveAlertDialogConfig;
  saveDashboardDialogConfig?:    SaveDashboardDialogConfig;
  getDownloadConfig?:            () => DownloadConfig;
  buttonSize?:                   'lg' | 'sm' | 'med' | undefined;
  plots?:                        Array<PlotModel>;
  testId?:                       ELEMENT;
  buttonTestId?:                 ELEMENT;
  shouldFetchShortUrlAfterOpen?: boolean;
  dropdownClickTrigger?:         () => void;
  copyLinkUrl?:                  string;
  savedSearchUrl?:               string;
  downloadDisabled?:             boolean;
  alignRight?:                   boolean;
};

/**
 * This component is a configurable dropdown that supports search-related actions. There are seven that are
 * pre-configured; each has an enum identifier. A caller supplies a list of sections for the dropdown. Each item
 * in a section can either be an action identifier, or a custom configuration.
 *
 * @prop text                            text for the dropdown button
 * @prop className                       optional class(es) for the button
 * @prop variant                         variant for dropdown button
 * @prop leftIcon                        icon for the left side of the dropdown button
 * @prop sections                        a list of sections, each of which is a list of menu items
 * @prop saveSearchDialogConfig          config for the "Save search" dialog
 * @prop saveAlertDialogConfig           config for the "Save as alert" dialog
 * @prop saveDashboardDialogConfig       config for the "Save to dashboard" dialog
 * @prop getDownloadConfig               callback that returns download config
 * @prop buttonSize                      size of the dropdown button
 * @prop testId                          identifying string to use for as base for action test IDs
 * @prop buttonTestId                    identifying string to use for dropdown button
 * @prop shouldFetchShortUrlAfterOpen    in cases where copy link functionality is presented in dropdown
 * @prop dropdownClickTrigger            action which is called when dropdown is clicked
 * @prop copyLinkUrl                     URL to copy if it's not the same as the current URL
 * @prop savedSearchUrl                  URL to save if it's not the same as the current URL
 * @prop downloadDisabled                if true, disable the Download button
 * @prop alignRight                      if true, aligns the menu end with the right edge of the button
 */
function SearchActionsDropdown({
  text,
  className,
  variant,
  leftIcon,
  sections,
  saveAlertDialogConfig = {},
  buttonSize,
  getDownloadConfig,
  testId,
  buttonTestId,
  shouldFetchShortUrlAfterOpen,
  dropdownClickTrigger,
  copyLinkUrl,
  savedSearchUrl = '',
  downloadDisabled = false,
  alignRight = false,
}: SearchActionsDropdownProps): JSX.Element {

  const saveSearchDialogInitialState = { visible: false, searchType: SavedSearchType.PRIVATE, savedSearchUrl: '' };
  const [ saveSearchDialogState, setSaveSearchDialogState ] = useState(saveSearchDialogInitialState);
  const [ showSaveAsAlertDialog, setShowSaveAsAlertDialog ] = useState(false);
  const [ showSaveToDashboardDialog, setShowSaveToDashboardDialog ] = useState(false);
  const absoluteUrl = useRecoilValue(absoluteResolvedUrlState);
  const completeFilter = useRecoilValue(completeFilterState);
  const bulkExportsPageUrl = useRecoilValue(bulkExportsPageUrlState);
  const [ createAlert ] = useMutation(CREATE_ALERT);

  const alertPageAlerts = useAlertPageAlerts();
  const [ alertToSave, setAlertToSave ] = useState<AlertInput>(DEFAULT_ALERT);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const {
    previousShortenedUrl,
    copyLinkIsAvailable,
    copyShortLink,
    fetchShortUrlForSafari,
  } = useCopyShortLink(copyLinkUrl || absoluteUrl);

  const openSaveAlertDialog = (): void => {
    if (!completeFilter) {
      setPageAlertConfig({
        message: 'Please enter a search string or other filter expression and try again.',
        autoHide: true,
      });
      return;
    }
    const newAlertToSave = clone(DEFAULT_ALERT);
    newAlertToSave.trigger = completeFilter;
    setAlertToSave(newAlertToSave);
    setShowSaveAsAlertDialog(true);
  };

  const closeSaveAsAlertDialog = (): void => {
    setShowSaveAsAlertDialog(false);
  };

  const handleSaveAsAlert = (alertInput: AlertInput): void => {
    createAlert({ variables: { alert: alertInput } })
      .then(closeSaveAsAlertDialog)
      .then(alertPageAlerts.showCreateSuccess)
      .catch(alertPageAlerts.showError);
  };

  // default configuration for each predefined search action
  const DEFAULT_ITEM_CONFIG: { [action in SearchActions]: MenuItemConfig } = {
    [SearchActions.SAVE]: {
      text:         'Save Search',
      clickHandler: (): void => setSaveSearchDialogState({ visible: true, searchType: SavedSearchType.PRIVATE, savedSearchUrl }),
      testId:       createTestId(testId, 'save-search'),
    },
    [SearchActions.SAVE_SHARED]: {
      text:         'Save to Shared Searches',
      clickHandler: (): void => setSaveSearchDialogState({ visible: true, searchType: SavedSearchType.SHARED, savedSearchUrl }),
      testId:       createTestId(testId, 'save-shared-search'),
      permission:   Permission.userConfigWrite,
    },
    [SearchActions.ALERT]: {
      text:         'Save as Alert',
      clickHandler: openSaveAlertDialog,
      testId:       createTestId(testId, 'save-as-alert'),
      permission:   Permission.userConfigWrite,
    },
    [SearchActions.DASHBOARD]: {
      text:         'Save to Dashboard',
      clickHandler: (): void => setShowSaveToDashboardDialog(true),
      testId:       createTestId(testId, 'save-to-dashboard'),
      permission:   Permission.userConfigWrite,
    },
    [SearchActions.DOWNLOAD]: {
      text:         'Download',
      testId:       createTestId(testId, 'download'),
      clickHandler: (): void => {
        if (getDownloadConfig) {
          downloadFile(getDownloadConfig());
        }
      },
      disabled: downloadDisabled,
    },
    [SearchActions.DOWNLOAD_CSV]: {
      text:         'Download CSV',
      testId:       createTestId(testId, 'download-csv'),
      clickHandler: (): void => {
        if (getDownloadConfig) {
          downloadFile(getDownloadConfig());
        }
      },
      disabled: downloadDisabled,
    },
    [SearchActions.EXPORT]: {
      text:   'Export to S3',
      href:   bulkExportsPageUrl,
      as:     Link,
      testId: createTestId(testId, 'export'),
    },
    [SearchActions.COPY_LINK]: {
      text:         copyLinkIsAvailable ? 'Copy Link' : 'Preparing Link To Share',
      clickHandler: copyShortLink,
      testId:      createTestId(testId, 'copy-link'),
      disabled:     !copyLinkIsAvailable,
    },
  };

  // translate the user-provided sections into the format that Dropdown takes
  const menuSections = sections.map((section) => section.map((item: string | ActionMenuItemConfig) => {
    if (isString(item)) {
      return DEFAULT_ITEM_CONFIG[item as SearchActions];
    }
    if (item.action) {
      const itemConfig: ActionMenuItemConfig = clone(DEFAULT_ITEM_CONFIG[item.action]) as ActionMenuItemConfig;
      keys(item).forEach((key) => {
        const itemKey = key as keyof ActionMenuItemConfig;
        // apply user-supplied overrides; could not get line below to compile in TS
        if (itemConfig && item[itemKey]) {
          itemConfig[itemKey] = item[itemKey];
        }
      });
      return itemConfig;
    }
    return item;
  }));

  const saveSearchDialog = (
    <SaveSearchDialog
      searchType={saveSearchDialogState.searchType}
      savedSearchUrl={saveSearchDialogState.savedSearchUrl}
      show={saveSearchDialogState.visible}
      close={(): void => setSaveSearchDialogState(((prevState) => ({ ...prevState, visible: false })))}
    />
  );

  const saveAlertDialog = (
    <SaveAlertDialog
      alert={alertToSave}
      title={saveAlertDialogConfig.title || 'Save Alert'}
      show={showSaveAsAlertDialog}
      close={closeSaveAsAlertDialog}
      triggerType={saveAlertDialogConfig.triggerType}
      handleSubmit={handleSaveAsAlert}
    />
  );

  const saveToDashboardDialog = (
    <SaveToDashboard
      show={showSaveToDashboardDialog}
      close={(): void => setShowSaveToDashboardDialog(false)}
    />
  );

  const dropdownClickTriggerLocal =  (): void => {
    if (isSafari) {
      const urlToShorten = copyLinkUrl || absoluteUrl;
      if (shouldFetchShortUrlAfterOpen && previousShortenedUrl !== urlToShorten) {
        fetchShortUrlForSafari();
      }
    }
  };

  return (
    <>
      <Dropdown
        className={className}
        variant={variant}
        sections={menuSections}
        leftIcon={leftIcon}
        size={buttonSize}
        testId={buttonTestId}
        alignRight={alignRight}
        dropdownClickTrigger={dropdownClickTrigger || dropdownClickTriggerLocal}
      >
        {text}
      </Dropdown>
      { saveSearchDialog }
      { saveAlertDialog }
      { saveToDashboardDialog }
    </>
  );
}

export default SearchActionsDropdown;
